import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { useCurrency } from '../contexts/CurrencyContext';

/**
 * CashflowSnapshotWidget — "Cashflow at a glance" tile combining the
 * Debtors-Aging (receivables) and Creditors-Aging (payables) reports
 * into a single side-by-side card.
 *
 * Receivables come back in the company's reporting currency.
 * Payables are returned per supplier-currency, so we render a chip per
 * currency and additionally compute a Net Working-Capital figure for
 * the company-currency slice only (the only honest single-currency net).
 */
const fmt = (n) =>
  (parseFloat(n) || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 3,
  });

const CashflowSnapshotWidget = () => {
  const { companySettings } = useCurrency();
  const companyCcy = companySettings?.currency || 'BHD';
  const [debtors, setDebtors] = useState(null);
  const [creditors, setCreditors] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [d, c] = await Promise.all([
          api.get('/sales/reports/debtors-aging'),
          api.get('/supplier-payments/reports/creditors-aging'),
        ]);
        if (!cancelled) {
          setDebtors(d.data);
          setCreditors(c.data);
        }
      } catch {
        if (!cancelled) {
          setDebtors(null);
          setCreditors(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div
        className="bg-white rounded-lg border border-slate-200 p-4 mb-4"
        data-testid="cashflow-snapshot-widget"
      >
        <div className="text-sm text-slate-500">Loading cashflow snapshot…</div>
      </div>
    );
  }

  const receivablesTotal = debtors?.totals?.pending_total || 0;
  const receivablesBills = debtors?.totals?.pending_bills || 0;
  const payablesByCcy = creditors?.totals_by_currency || [];
  const payablesCompanyCcy =
    (payablesByCcy.find((t) => (t.currency || '').toUpperCase() === companyCcy.toUpperCase())
      ?.pending_total) || 0;
  const totalPayableBills = payablesByCcy.reduce((a, t) => a + (t.pending_bills || 0), 0);
  const netCompanyCcy = receivablesTotal - payablesCompanyCcy;
  const netPositive = netCompanyCcy >= 0;
  // Flag whenever any payable is in a non-company currency — including the
  // edge case where ALL payables are foreign (in which case netCompanyCcy
  // would otherwise look misleadingly positive).
  const hasForeignPayables = payablesByCcy.some(
    (t) => (t.currency || '').toUpperCase() !== companyCcy.toUpperCase(),
  );

  return (
    <div
      className="bg-white rounded-lg border border-slate-200 p-4 mb-4"
      data-testid="cashflow-snapshot-widget"
    >
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-sm font-semibold text-slate-900">💰 Cashflow at a Glance</div>
          <div className="text-[11px] text-slate-500">
            Receivables vs Payables · As of {debtors?.as_of || creditors?.as_of || '—'}
          </div>
        </div>
        <div className="flex gap-1.5">
          <Link
            to="/sales-reports"
            className="text-xs px-2 py-1 rounded border border-emerald-600 text-emerald-700 hover:bg-emerald-50"
            data-testid="cashflow-debtors-link"
          >
            Debtors →
          </Link>
          <Link
            to="/supplier-payments"
            className="text-xs px-2 py-1 rounded border border-rose-600 text-rose-700 hover:bg-rose-50"
            data-testid="cashflow-creditors-link"
          >
            Creditors →
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Receivables tile */}
        <div
          className="rounded-lg border border-emerald-200 bg-emerald-50 p-3"
          data-testid="cashflow-receivables-tile"
        >
          <div className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold">
            ⬅️ Money Coming In
          </div>
          <div className="text-xl font-mono font-bold text-emerald-900 mt-1">
            {fmt(receivablesTotal)} <span className="text-xs">{companyCcy}</span>
          </div>
          <div className="text-[11px] text-emerald-700 mt-1">
            {receivablesBills} pending bill{receivablesBills === 1 ? '' : 's'} across{' '}
            {(debtors?.rows || []).length} customer
            {(debtors?.rows || []).length === 1 ? '' : 's'}
          </div>
          {(debtors?.totals?.b_120_plus || 0) > 0 && (
            <div className="text-[11px] text-rose-700 mt-1.5 font-semibold">
              ⚠️ {fmt(debtors.totals.b_120_plus)} {companyCcy} over 120 days
            </div>
          )}
        </div>

        {/* Payables tile — one chip per supplier currency */}
        <div
          className="rounded-lg border border-amber-200 bg-amber-50 p-3"
          data-testid="cashflow-payables-tile"
        >
          <div className="text-[10px] uppercase tracking-wide text-amber-700 font-semibold">
            ➡️ Money Going Out
          </div>
          {payablesByCcy.length === 0 ? (
            <div className="text-xl font-mono font-bold text-amber-900 mt-1">
              0.00 <span className="text-xs">{companyCcy}</span>
            </div>
          ) : (
            <div className="mt-1 space-y-0.5">
              {payablesByCcy.map((t) => (
                <div
                  key={t.currency || '_'}
                  className="font-mono text-amber-900 text-sm"
                  data-testid={`cashflow-payables-${t.currency || 'none'}`}
                >
                  <b className="text-base">{fmt(t.pending_total)}</b>{' '}
                  <span className="text-xs">{t.currency || '—'}</span>
                </div>
              ))}
            </div>
          )}
          <div className="text-[11px] text-amber-700 mt-1">
            {totalPayableBills} pending PO{totalPayableBills === 1 ? '' : 's'} across{' '}
            {(creditors?.rows || []).length} supplier
            {(creditors?.rows || []).length === 1 ? '' : 's'}
          </div>
        </div>

        {/* Net Position tile (company-currency slice only) */}
        <div
          className={`rounded-lg border p-3 ${
            netPositive
              ? 'border-cyan-200 bg-cyan-50'
              : 'border-rose-200 bg-rose-50'
          }`}
          data-testid="cashflow-net-tile"
        >
          <div
            className={`text-[10px] uppercase tracking-wide font-semibold ${
              netPositive ? 'text-cyan-700' : 'text-rose-700'
            }`}
          >
            {netPositive ? '📈 Net Working Capital' : '📉 Net Gap'} ({companyCcy})
          </div>
          <div
            className={`text-xl font-mono font-bold mt-1 ${
              netPositive ? 'text-cyan-900' : 'text-rose-900'
            }`}
            data-testid="cashflow-net-amount"
          >
            {netPositive ? '+' : ''}
            {fmt(netCompanyCcy)} <span className="text-xs">{companyCcy}</span>
          </div>
          <div
            className={`text-[11px] mt-1 ${
              netPositive ? 'text-cyan-700' : 'text-rose-700'
            }`}
          >
            {fmt(receivablesTotal)} in − {fmt(payablesCompanyCcy)} out
            {hasForeignPayables && (
              <span className="block opacity-70">
                Foreign-currency payables shown separately (no FX mix).
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CashflowSnapshotWidget;
